import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  TextField,
  Typography,
  Grid,
  Switch,
  FormControlLabel,
  MenuItem
} from "@mui/material";

const IVA_OPTIONS = [
  { value: 0, label: "0%" },
  { value: 5, label: "5%" },
  { value: 15, label: "15%" },
  { value: 999, label: "No objeto de IVA" }
];

const AddProduct = ({ onProductDetailsChange, sectionSeq, categorySeq, brandSeq }) => {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [pvp1, setPvp1] = useState("");
  const [ivaPorcentaje, setIvaPorcentaje] = useState(15);
  const [stockMinimo, setStockMinimo] = useState("");
  const [estado, setEstado] = useState(true);
  const [ecommerce, setEcommerce] = useState(true);
  const [descripcion, setDescripcion] = useState("");
  const [barcode, setBarcode] = useState("");
  const [codigoAuxiliar, setCodigoAuxiliar] = useState("");
  const [seriado, setSeriado] = useState(false);

  useEffect(() => {
    if (sectionSeq && categorySeq && brandSeq) {
      const base = `${sectionSeq}${categorySeq}${brandSeq}`;
      async function fetchNext() {
        const response = await fetch(`/productos/next/?base=${base}`);
        const data = await response.json();
        if (data.next_code) setCode(data.next_code);
        else setCode(base);
      }
      fetchNext();
    }
  }, [sectionSeq, categorySeq, brandSeq]);

  const ivaValor = useMemo(() => {
    const p = parseFloat((pvp1 || "0").replace(",", "."));
    const porc = ivaPorcentaje === 999 ? 0 : ivaPorcentaje;
    return (p * (porc / 100)).toFixed(6);
  }, [pvp1, ivaPorcentaje]);

  const precioFinal = useMemo(() => {
    const p = parseFloat((pvp1 || "0").replace(",", "."));
    return (p + parseFloat(ivaValor || 0)).toFixed(6);
  }, [pvp1, ivaValor]);

  useEffect(() => {
    onProductDetailsChange({
      code,
      name,
      pvp1,
      iva_porcentaje: ivaPorcentaje,
      iva_valor: ivaValor,
      precio_final: precioFinal,
      stock_minimo: stockMinimo,
      estado: estado ? "activo" : "inactivo",
      ecommerce,
      description: descripcion,
      barcode,
      codigo_auxiliar: codigoAuxiliar,
      seriado
    });
  }, [
    code,
    name,
    pvp1,
    ivaPorcentaje,
    ivaValor,
    precioFinal,
    stockMinimo,
    estado,
    ecommerce,
    descripcion,
    barcode,
    codigoAuxiliar,
    seriado,
    onProductDetailsChange
  ]);

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: "#1565C0" }}>
        Información del Producto
      </Typography>

      <Typography variant="subtitle2" sx={{ mb: 3, color: "black", fontWeight: "bold" }}>
        Los campos con (*) son obligatorios
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            label="Nombre del Producto"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Código"
                value={code}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Código de barras"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Código auxiliar"
                value={codigoAuxiliar}
                onChange={(e) => setCodigoAuxiliar(e.target.value)}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={seriado}
                    onChange={(e) => setSeriado(e.target.checked)}
                    color="primary"
                  />
                }
                label="¿Producto seriado?"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={estado}
                    onChange={(e) => setEstado(e.target.checked)}
                    color="primary"
                  />
                }
                label={estado ? "Activo" : "Inactivo"}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={ecommerce}
                    onChange={(e) => setEcommerce(e.target.checked)}
                    color="primary"
                  />
                }
                label={ecommerce ? "Visible en e-commerce" : "No visible en e-commerce"}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="PVP1"
                value={pvp1}
                onChange={(e) => setPvp1(e.target.value.replace(/,/g, "."))}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                label="IVA %"
                value={ivaPorcentaje}
                onChange={(e) => setIvaPorcentaje(parseInt(e.target.value, 10))}
              >
                {IVA_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="IVA $"
                value={ivaValor}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Precio Final"
                value={precioFinal}
                InputProps={{ readOnly: true }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Stock mínimo"
                value={stockMinimo}
                onChange={(e) => setStockMinimo(e.target.value)}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            required
            multiline
            rows={8}
            label="Descripción"
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddProduct;